import { formatTrackTimeLabel, formatTrackTimestamp, parseGeoPoint } from '../utils/formatters';
import type { LatLng, MapMarker, TrackPoint } from './types';

export interface TripMapPathDisplay {
  track?: LatLng[];
  route?: { from: LatLng; to: LatLng };
  mode: 'track' | 'planned' | 'none';
}

/** Prefer GPS track when available; otherwise show planned route between stops. */
export function resolveTripMapPathDisplay(
  trackPoints: Array<LatLng | TrackPoint>,
  pickup: LatLng | null,
  dropoff: LatLng | null
): TripMapPathDisplay {
  if (trackPoints.length >= 2) {
    return {
      track: trackPoints.map((p) => ({ lat: p.lat, lng: p.lng })),
      mode: 'track',
    };
  }
  if (pickup && dropoff) {
    return { route: { from: pickup, to: dropoff }, mode: 'planned' };
  }
  return { mode: 'none' };
}

export function locationLogsToTrack(
  logs: Array<{ geopoint?: string | null }>
): LatLng[] {
  return locationLogsToTrackPoints(logs).map((p) => ({ lat: p.lat, lng: p.lng }));
}

export function locationLogsToTrackPoints(
  logs: Array<{ geopoint?: string | null; timestamp?: string }>
): TrackPoint[] {
  return logs
    .map((log) => {
      const position = log.geopoint ? parseGeoPoint(log.geopoint) : null;
      if (!position || !log.timestamp) return null;
      return { ...position, timestamp: log.timestamp };
    })
    .filter((p): p is TrackPoint => p != null);
}

/** Build map markers including each historical GPS sample with acquisition time. */
export function buildTripMapMarkers(options: {
  pickup: LatLng | null;
  dropoff: LatLng | null;
  vehicle: LatLng | null;
  trackPoints: TrackPoint[];
  showTrackPoints?: boolean;
}): MapMarker[] {
  const list: MapMarker[] = [];
  const { pickup, dropoff, vehicle, trackPoints, showTrackPoints = true } = options;

  if (pickup) {
    list.push({
      id: 'pickup',
      position: pickup,
      title: 'Pickup',
      label: 'P',
      role: 'pickup',
    });
  }
  if (dropoff) {
    list.push({
      id: 'dropoff',
      position: dropoff,
      title: 'Drop-off',
      label: 'D',
      role: 'dropoff',
    });
  }
  if (vehicle) list.push({ id: 'vehicle', position: vehicle, title: 'Vehicle', role: 'vehicle' });

  if (showTrackPoints && trackPoints.length >= 2) {
    trackPoints.forEach((point, index) => {
      const acquiredAt = formatTrackTimestamp(point.timestamp);
      list.push({
        id: `gps-${index}`,
        position: point,
        title: `GPS ${index + 1}`,
        subtitle: acquiredAt,
        label: formatTrackTimeLabel(point.timestamp),
        role: 'track',
      });
    });
  }

  return list;
}

export function describeRoutePlanningError(provider: string, statusOrMessage: string): string {
  const status = statusOrMessage.toUpperCase();
  if (status.includes('REQUEST_DENIED') || status.includes('INVALID_REQUEST')) {
    return 'Maps API rejected the route request. Check API key and Directions API enablement in Admin → Settings.';
  }
  if (status.includes('ZERO_RESULTS') || status.includes('NOT_FOUND')) {
    return 'No drivable route found between pickup and drop-off. Verify addresses and coordinates.';
  }
  if (status.includes('OVER_QUERY_LIMIT') || status.includes('QUOTA')) {
    return 'Maps route quota exceeded. Try again later or check billing.';
  }
  if (status.includes('NO_ROUTE') || status.includes('FAIL')) {
    return 'Route planning failed. Pickup and drop-off may be unreachable by road.';
  }
  return `Route planning failed (${provider}): ${statusOrMessage}`;
}
